<template>
  <div
    v-for="(contact, index) in contacts"
    :key="index"
    class="contact-details"
  >
    <SoButton
      v-if="contacts.length > 1"
      type="icon"
      class="contact-details__remove-contact"
      icon="close"
      @click="removeContact"
    />
    <label class="contact-details__inline-input">
      Title
      <SoSelect
        v-model="contact.title"
        :items="titleOptions"
        data-testid="title-select"
        @update:model-value="updateField('title', index, $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      First Name
      <SoTextField
        :model-value="contact.firstName"
        :rules="[rules.required]"
        type="text"
        @update:model-value="updateField('firstName', index, $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Last Name
      <SoTextField
        :model-value="contact.lastName"
        :rules="[rules.required]"
        type="text"
        @update:model-value="updateField('lastName', index, $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Email
      <SoTextField
        :model-value="contact.email"
        :rules="[rules.email, rules.required]"
        type="text"
        @update:model-value="updateField('email', index, $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Mobile phone
      <SoTextField
        :model-value="contact.phone2"
        :rules="[rules.phoneNumber, atLeastOnePhone(contact)]"
        data-testid="mobile-phone"
        @update:model-value="updateField('phone2', index, $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Landline phone (optional)
      <SoTextField
        :model-value="contact.phone1"
        :rules="[rules.phoneNumber]"
        data-testid="landline-phone"
        @update:model-value="updateField('phone1', index, $event)"
      />
    </label>
    <div>
      <SoCheckbox
        v-if="contacts.length > 1"
        data-testid="primary-contact-checkbox"
        :model-value="contact.juniferPrimaryContact"
        :disabled="
          indexOfPrimarySelected !== null && indexOfPrimarySelected !== index
        "
        label="Is primary contact"
        @update:model-value="
          updateField('juniferPrimaryContact', index, $event)
        "
      />
      <SoCheckbox
        :model-value="contact.receiveEmail"
        label="Marketing Consent"
        @update:model-value="updateField('receiveEmail', index, $event)"
      />
      <small>Opt in to tariff deals, energy saving tips and more</small>
    </div>
  </div>
  <div class="contact-details__inline-input">
    <SoButton
      v-if="contacts.length <= 5"
      title="Add Contact"
      data-testid="add-contact-btn"
      class="pa-1 d-flex mt-4"
      prepend-icon="plus"
      large
      @click="addContact"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { cloneDeep } from "lodash"
  import { IContact } from "@/models/contacts/contact.types"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import rules from "@/validators/rules"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { TITLE_OPTIONS } from "@/constants/titleOptions"

  const emptyContact: Partial<IContact> = {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phone1: "",
    phone2: "",
    juniferPrimaryContact: false,
  }

  export default defineComponent({
    components: {
      SoSelect,
      SoButton,
      SoTextField,
      SoCheckbox,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Object as PropType<IContact[]>,
        default: () => ({}),
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        contacts: [emptyContact] as Partial<IContact>[],
        titleOptions: TITLE_OPTIONS,
        indexOfPrimarySelected: null as null | number,
      }
    },
    computed: {
      rules() {
        return rules
      },
    },
    created() {
      if (Array.isArray(this.modelValue) && this.modelValue.length > 0) {
        this.contacts = cloneDeep(this.modelValue)
      }
    },
    methods: {
      updateField(fieldName: string, index: number, fieldValue: string) {
        const value =
          fieldName === "phone1" || fieldName === "phone2"
            ? fieldValue.replace(/\s+/g, "")
            : fieldValue

        this.contacts = this.contacts.map((contact, contactIndex) => {
          this.disablePrimaryContact(fieldName, index, fieldValue)
          if (contactIndex === index) {
            return { ...contact, [fieldName]: value }
          }

          return contact
        })

        this.$emit("update:modelValue", this.contacts)
      },
      atLeastOnePhone(contact: Partial<IContact>) {
        return () =>
          !!contact.phone1 ||
          !!contact.phone2 ||
          "Enter a mobile or landline number"
      },
      addContact() {
        if (this.contacts.length === 1) {
          this.contacts[0] = {
            ...this.contacts[0],
            juniferPrimaryContact: true,
          }
          this.indexOfPrimarySelected = 0
          this.contacts = [this.contacts[0], emptyContact]

          return
        }

        this.contacts = [...this.contacts, emptyContact]
      },
      removeContact(index: number) {
        this.contacts.splice(index, 1)
      },
      disablePrimaryContact(
        fieldName: string,
        index: number,
        fieldValue: string,
      ) {
        if (fieldName === "juniferPrimaryContact" && fieldValue) {
          this.indexOfPrimarySelected = index
        }
        if (fieldName === "juniferPrimaryContact" && !fieldValue) {
          this.indexOfPrimarySelected = null
        }
      },
    },
  })
</script>

<style scoped>
  .contact-details {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: 4px;
    margin: 30px 0;
    padding: 40px 20px 20px 20px;
  }
  .contact-details__remove-contact {
    position: absolute;
    top: 5px;
    right: 5px;
  }
</style>
